/**
 * This file defines a set of reusable metadata consts w/o EC dependencies,and is bundled in the main chunk
 * Make sure you don't introduce any EC dependencies here.
 */

import { ISelectOption } from 'app/interfaces/ISelectOption';
import { IRadioOption } from 'app/interfaces/IRadioOption';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { copyBehaviorOptions, copyBehaviorOptionValues } from 'app/shared/constants/copy-behavior.const';
import { format, formatStringForAppType, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from './adf-model-type-map-const';
import {
    dynamicsDeploymentTypes,
    servicePrincipalCredentialTypes
} from 'app/model/linkedservice/dynamics.linkedservice';
import { isolationLevelConst } from './sql-const';
import { Constants, MAX_INT32 } from 'app/shared/shared-constants';
import { columnDelimiterWithoutNoneV2Options } from 'app/shared/components/fileformat/fileformat.constants';
import { BlobTypeStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/blob-type-store-read-settings';
import {
    shellStringResources,
    FormatDatasetV2,
    CredentialTypeOptions,
    dynamicsAuthTypes,
    azureDefaultAuthRefTypes,
    azureCloudTypeConst,
    ModelConstants,
    Type,
    SqlAdditionalPropertiesKeyType,
    azureSqlAuthRefTypes,
    SqlServerAuthType,
    copySourceReadBehaviors,
} from 'shell-models-lib';
import { TarGZipCompressionReadSettings } from 'app/model/pipeline/copysource/compression-read-settings/tar-gzip-compression-read-settings';
import { ZipDeflateCompressionReadSettings } from 'app/model/pipeline/copysource/compression-read-settings/zip-deflate-compression-read-settings';
import { TarCompressionReadSettings } from 'app/model/pipeline/copysource/compression-read-settings/tar-compression-read-settings';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { ISelectSwitchMetadata } from 'app/extensibility/metadatas/control/select-switch-metadata';
import { DirectionalHint } from '@fluentui/react/lib/common/DirectionalHint';
import { mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { GlobalVariables } from 'app/services/globals';
import { blockSizeInMbMetadata } from '../linked-service/linked-service-shared-fields';
import { compact } from 'lodash';
import { driverVersions, driverVersionVersionOptions } from './tabular-consts';
import { TableActionOption, TableActionOptionTypes } from 'app/model/pipeline/copysink/lakehouse-table.copysink';
import { ColumnType } from 'app/shared/utils/column-type.util';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { type IParameterizableMetadata } from 'app/extensibility/metadatas/control/parameterizable-metadata';

interface IDisableChunkingSettings {
    infoLink: string;
}

export type ResourceSelectionMethod = 'LoginUser' | 'ServicePrincipal' | 'ManagedIdentity';

export const azureResourceSelectMethodOptions: Array<IRadioOption<'azure' | 'manual'>> = [
    {
        label: stringResources.FromAzureSubscriptionLabel,
        value: 'azure'
    },
    {
        label: stringResources.EnterManuallyLabel,
        value: 'manual'
    }
];

export const dynamicRangePartitionControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'columns-selection',
            bind: 'partitionColumnName',
            label: stringResources.ModelName_Partition_ColumnName,
            multiple: false,
            includeCopySourceProperties: true,
            allowedColumnTypes: ['Int32', 'Int64', 'DateTime', 'DateTimeOffset'],
            excludeSourceProperties: ['partitionOption', 'partitionSettings']
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'partitionUpperBound',
            label: stringResources.ModelName_Partition_UpperBound,
            description: stringResources.SQLPartitionUpperBound_Description
        },
        dynamicContentTextClass: 'input-spaced'
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'partitionLowerBound',
            label: stringResources.ModelName_Partition_LowerBound,
            description: stringResources.SQLPartitionLowerBound_Description
        },
        dynamicContentTextClass: 'input-spaced'
    }
];

export function getLakehouseTableActionOptions(
    showUpsert: boolean = false,
    isOldResource: boolean = false
): Array<IRadioOption<TableActionOptionTypes>> {
    return showUpsert
        ? [
            {
                label: stringResources.SinkTransformationSavePolicyAppend,
                value: TableActionOption.Append,
                description: stringResources.Lakehouse_Table_Action_Append_Description
            },
            {
                label: stringResources.Overwrite,
                value: isOldResource ? TableActionOption.Overwrite : TableActionOption.OverwriteSchema,
                description: stringResources.DataFlowSourcePanelDeltaOverwriteDescription
            },
            {
                label: stringResources.WriteBehavior_Option_Upsert,
                value: TableActionOption.Upsert,
                description: stringResources.Lakehouse_Table_Action_Upsert_Description
            }
        ]
        : [
            {
                label: stringResources.SinkTransformationSavePolicyAppend,
                value: TableActionOption.Append,
                description: stringResources.Lakehouse_Table_Action_Append_Description
            },
            {
                label: stringResources.Overwrite,
                value: isOldResource ? TableActionOption.Overwrite : TableActionOption.OverwriteSchema,
                description: stringResources.DataFlowSourcePanelDeltaOverwriteDescription
            }
        ];
}

export const authenticationReferenceMethodOptions: Array<IRadioOption<string>> = [
    {
        label: stringResources.Inline,
        value: azureDefaultAuthRefTypes.InlineRefTypeAuthentication
    },
    {
        label: stringResources.ModelName_Credential,
        value: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication
    }
];

export function getAuthHeader(showAuthorization: boolean): ControlMetadata {
    const options: Array<ISelectOption<string>> = [
        {
            label: 'X-API-Key',
            value: 'X-API-Key'
        }
    ];

    if (showAuthorization) {
        options.push({
            label: 'Authorization', // display the actual object key, do not need localization
            value: 'Authorization'
        });
    }
    options.push({
        label: 'APIKey',
        value: 'APIKey'
    });

    return {
        type: 'key-val-list',
        bind: 'authHeaders',
        label: stringResources.ModelName_AuthHeaders,
        description: stringResources.AuthHeaders_Description,
        keyOptions: options,
        keyColumnTitle: stringResources.Name,
        keyType: 'dropdown',
        dropDownEditable: true,
        isValueSecret: true,
        supportAkv: true
    };
}

export function getAccessKeySecretUrl(displayText: string, requiredUrl: boolean): ControlMetadata[] {
    return [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'serviceUrl',
                label: stringResources.ModelName_ServiceUrl,
                required: true,
                placeholder: requiredUrl
                    ? 'https://<namespace>.compat.objectstorage.<region identifier>.oraclecloud.com'
                    : '',
                isLabelVertical: true,
                validators: [{ type: 'required', errorMessage: '' }],
                warningMessageCalculator: (value: string) =>
                    stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
            },
            warningMessageCalculator: (value: string) =>
                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
        },
        {
            type: 'dynamic-akv-switcher',
            enableAKVParameterization: true,
            bind: 'accessKeyId',
            label: stringResources.ModelName_AccessKeyID,
            child: {
                type: 'parameterizable',
                attribute: 'accessKeyId',
                child: {
                    type: 'textbox',
                    label: stringResources.ModelName_AccessKeyID,
                    required: true,
                    isLabelVertical: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            },
            description: format(
                stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, displayText),
                stringResources.ModelName_AccessKeyID
            )
        },
        {
            type: 'dynamic-akv-switcher',
            enableAKVParameterization: true,
            bind: 'secretAccessKey',
            label: stringResources.ModelName_AmazonAccessKey,
            child: {
                type: 'parameterizable',
                attribute: 'secretAccessKey',
                disableAddParameter: true,
                child: {
                    type: 'password',
                    label: stringResources.ModelName_AmazonAccessKey,
                    required: true,
                    isLabelVertical: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            },
            description: format(
                stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, displayText),
                stringResources.ModelName_AmazonAccessKey
            )
        }
    ];
}

export enum FilePathMode {
    DATASET,
    WILDCARD,
    PREFIX,
    LIST,
    FILEFILTER,
    NameRange
}

export const readBehaviors = copySourceReadBehaviors;

export const tableOptionValues = {
    autoCreate: 'autoCreate'
};

export const partitionOptionConst = {
    none: 'None',

    dyamicRange: 'DynamicRange',

    sapHanaDynamicRange: 'SapHanaDynamicRange',

    dataSlice: 'DataSlice',

    hash: 'Hash',

    physicalPartitionsOfTable: 'PhysicalPartitionsOfTable',

    partitionOnInt: 'PartitionOnInt',
    partitionOnCalendarYear: 'PartitionOnCalendarYear',
    partitionOnCalendarDate: 'PartitionOnCalendarDate',
    partitionOnCalendarMonth: 'PartitionOnCalendarMonth',
    partitionOnTime: 'PartitionOnTime',

    autoDetect: 'AutoDetect'
};

export const partitionOptions: Array<IRadioOption<string>> = [
    {
        label: stringResources.None,
        value: partitionOptionConst.none
    },
    {
        label: stringResources.PartitionOptionType_PhysicalPartitionOfTable,
        value: partitionOptionConst.physicalPartitionsOfTable,
        description: format(
            stringResources.Partition_Physical_Tooltip_Template,
            '(?AdfTabularPartitionName)',
            'SELECT * FROM <TableName> PARTITION("?AdfTabularPartitionName")'
        )
    },
    {
        label: stringResources.PartitionOptionType_DynamicRange,
        value: partitionOptionConst.dyamicRange,
        description: format(
            stringResources.Partition_DynamicRange_Tooltip_Template_s,
            '(?AdfRangePartitionColumnName, ?AdfRangePartitionLowbound, ?AdfRangePartitionUpbound)',
            'SELECT * FROM <TableName> WHERE ?AdfRangePartitionColumnName <= ?AdfRangePartitionUpbound AND ?AdfRangePartitionColumnName >= ?AdfRangePartitionLowbound'
        )
    },
    {
        label: stringResources.PartitionOptionType_DataSlice,
        value: partitionOptionConst.dataSlice,
        description: format(
            stringResources.Partition_DataSlice_Tooltip_Template,
            'SELECT MAX(datasliceid) from <TableName>',
            'SELECT * FROM <TableName> WHERE mod(datasliceid, ?AdfPartitionCount) = ?AdfDataSliceCondition'
        )
    },
    {
        label: stringResources.HashLabel,
        value: partitionOptionConst.hash,
        description: format(
            stringResources.Partition_Hash_Tooltip_Template,
            'select * from <TableName> where ?AdfHashPartitionCondition'
        )
    },
    {
        label: stringResources.PartitionOptionType_SapPartitionOnInt,
        value: partitionOptionConst.partitionOnInt
    },
    {
        label: stringResources.PartitionOptionType_SapPartitionOnCalendarYear,
        value: partitionOptionConst.partitionOnCalendarYear
    },
    {
        label: stringResources.PartitionOptionType_SapPartitionOnCalendarMonth,
        value: partitionOptionConst.partitionOnCalendarMonth
    },
    {
        label: stringResources.PartitionOptionType_SapPartitionOnCalendarDate,
        value: partitionOptionConst.partitionOnCalendarDate
    },
    {
        label: stringResources.PartitionOptionType_SapPartitionOnTime,
        value: partitionOptionConst.partitionOnTime
    }
];

const sapHanaSqlSourceSharedPartitionOptions: Array<IRadioOption<string>> = [
    {
        label: stringResources.None,
        value: partitionOptionConst.none
    },
    {
        label: stringResources.PartitionOptionType_PhysicalPartitionOfTable,
        value: partitionOptionConst.physicalPartitionsOfTable,
        description: stringResources.Partition_SapSql_Physical_Tooltip
    }
];

export const sapHanaPartitionOptions: Array<IRadioOption<string>> = [
    ...sapHanaSqlSourceSharedPartitionOptions,
    {
        label: stringResources.PartitionOptionType_DynamicRange,
        value: partitionOptionConst.sapHanaDynamicRange,
        description: format(
            stringResources.Partition_DynamicRange_Tooltip_Template,
            '(?AdfHanaDynamicRangePartitionCondition)',
            'SELECT * FROM <TableName> WHERE ?AdfHanaDynamicRangePartitionCondition'
        )
    }
];

export const sqlSourcePartitionOptions: Array<IRadioOption<string>> = [
    ...sapHanaSqlSourceSharedPartitionOptions,
    {
        label: stringResources.PartitionOptionType_DynamicRange,
        value: partitionOptionConst.dyamicRange,
        description: format(
            stringResources.Partition_DynamicRange_Tooltip_Template,
            '(?DfDynamicRangePartitionCondition)',
            'SELECT * FROM <TableName> WHERE ?DfDynamicRangePartitionCondition'
        )
    }
];

export const azurePostgreSqlPartitionOptions: Array<IRadioOption<string>> = [
    {
        label: stringResources.None,
        value: partitionOptionConst.none
    },
    {
        label: stringResources.PartitionOptionType_PhysicalPartitionOfTable,
        value: partitionOptionConst.physicalPartitionsOfTable,
        description: format(
            stringResources.Partition_Physical_Tooltip_Template,
            '(?AdfTabularPartitionName)',
            'SELECT * FROM <TableName> ?AdfTabularPartitionName'
        )
    },
    {
        label: stringResources.PartitionOptionType_DynamicRange,
        value: partitionOptionConst.dyamicRange,
        description: format(
            stringResources.Partition_DynamicRange_Tooltip_Template_s,
            '(?AdfRangePartitionColumnName, ?AdfRangePartitionLowbound, ?AdfRangePartitionUpbound)',
            'SELECT * FROM <TableName> WHERE ?AdfRangePartitionColumnName <= ?AdfRangePartitionUpbound AND ?AdfRangePartitionColumnName >= ?AdfRangePartitionLowbound'
        )
    }
];

export const sqlAnalyticsEndpointPartitionOptions: Array<IRadioOption<string>> = [
    {
        label: stringResources.None,
        value: partitionOptionConst.none
    },
    {
        label: stringResources.PartitionOptionType_DynamicRange,
        value: partitionOptionConst.dyamicRange,
        description: format(
            stringResources.Partition_DynamicRange_Tooltip_Template_s,
            '(?DfDynamicRangePartitionCondition)',
            'SELECT * FROM <TableName> WHERE ?DfDynamicRangePartitionCondition'
        )
    }
];

export const readBehaviorOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.ReadBehaviorQueryLabel,
        value: readBehaviors.query
    },
    {
        label: stringResources.ReadBehaviorQueryAllLabel,
        value: readBehaviors.queryAll
    }
];

export function tableCreationOptions(isDW: boolean = false): Array<IRadioOption<string>> {
    return [
        {
            label: stringResources.UseExisting,
            value: undefined
        },
        {
            label: stringResources.Dataset_WriterTableOptionAutoCreateTable,
            value: tableOptionValues.autoCreate,
            description: isDW
                ? stringResources.Dataset_WriterAutoTableCreationDesc_DW
                : stringResources.Dataset_WriterAutoTableCreationDesc
        }
    ];
}

export const yesOrNoOptions: Array<IRadioOption<boolean>> = [
    {
        label: stringResources.Yes,
        value: true
    },
    {
        label: stringResources.No,
        value: false
    }
];

export function autoTableCreationMetadata(isDW: boolean = false): ControlMetadata {
    return {
        type: 'parameterizable',
        child: {
            type: 'radio',
            bind: 'tableOption',
            label: stringResources.Dataset_WriterTableOption,
            options: tableCreationOptions(isDW)
        },
        dynamicContentTextClass: 'input-spaced',
        mergeSettingsInTrident: __IS_FABRIC_DPE__
    };
}

export function preCopyScriptMetadata(description?: string): ControlMetadata {
    return GlobalVariables.enableDraftEditor_PreCopyScript
        ? {
            type: 'parameterizable',
            attribute: 'preCopyScript',
            child: null,
            mode: 2,
            label: stringResources.Dataset_PreScript_Label,
            description: description ?? format(stringResources.Dataset_PreCopyScript_Description, __IS_FABRIC_DPE__ ? stringResources.DestinationLabel.toLocaleLowerCase() : stringResources.Sink.toLocaleLowerCase())
        }
        : {
            type: 'parameterizable',
            child: {
                type: 'textarea',
                bind: 'preCopyScript',
                label: stringResources.Dataset_PreScript_Label,
                description: description ?? format(stringResources.Dataset_PreCopyScript_Description, __IS_FABRIC_DPE__ ? stringResources.DestinationLabel.toLocaleLowerCase() : stringResources.Sink.toLocaleLowerCase()),
                isLabelVertical: false
            },
            dynamicContentTextClass: 'input-spaced'
        };
}

export const sqlWriteBatchTimeoutMetadata: IParameterizableMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'writeBatchTimeout',
        label: stringResources.ModelName_WriteBatchTimeout,
        description: stringResources.ModelName_WriteBatchTimeout_Description,
        placeholder: 'e.g. 00:30:00',
        validators: [
            {
                type: 'pattern',
                pattern: Constants.timeoutValidationPattern,
                errorMessage: format(stringResources.InvalidTimeoutPattern, stringResources.ModelName_WriteBatchTimeout)
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const sqlWriteBatchSizeMetadata: IParameterizableMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox-number',
        bind: 'writeBatchSize',
        label: stringResources.ModelName_WriteBatchSize,
        description: stringResources.ModelName_WriteBatchSize_Description,
        inputType: 'number',
        min: 0
    },
    dynamicContentTextClass: 'input-spaced'
};

export const maxConcurrentConnectionsMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox-number',
        bind: 'maxConcurrentConnections',
        label: stringResources.ModelName_MaxConcurrentConnections,
        description: stringResources.MaxConcurrentConnections_Description,
        min: 0,
        validators: [
            {
                type: 'number',
                min: 0,
                errorMessage: stringResources.MaxConcurrentConnections_ValidatorErrorMessage
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const recursiveCheckboxMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'checkbox',
        bind: 'recursive',
        label: stringResources.Recursive_Label,
        defaultValue: true,
        description: stringResources.Recursive_Process_Description,
        labelPosition: 'left'
    },
    dynamicContentTextClass: 'input-spaced'
};

export const deleteFilesAfterCompletionMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'checkbox',
        bind: 'deleteFilesAfterCompletion',
        label: stringResources.DeleteFilesAfterCompletion_Label,
        defaultValue: false,
        labelPosition: 'left',
        description: stringResources.DeleteFileAfterCompletion_Description
    },
    dynamicContentTextClass: 'input-spaced',
    hintMessage: stringResources.Binary_Copy_DeleteFilesAfterCompletionHint,
    hideHintMessage: (owner: BlobTypeStoreReadSettings) => !owner || !owner.deleteFilesAfterCompletion
};

export const preserveZipFileAsFolderMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'checkbox',
        bind: 'preserveZipFileNameAsFolder',
        label: stringResources.ModelName_PreserveZipFileNameAsFolder,
        defaultValue: true,
        description: stringResources.PreserveCompressionFileNameAsFolder_Description,
        labelPosition: 'left'
    },
    hintMessage: stringResources.PreserveCompressionFileNameAsFolder_Tip,
    hideHintMessage: (owner: ZipDeflateCompressionReadSettings) => {
        return (
            !owner?.owner?.owner?.owner ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !((owner.owner.owner.owner as any).sourceDataset instanceof FormatDatasetV2) ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !(owner.owner.owner.owner as any).sourceDataset.location ||
            !(
                (
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (owner.owner.owner.owner as any).sourceDataset.location.isFolder() &&
                    owner.preserveZipFileNameAsFolder != null &&
                    !owner.preserveZipFileNameAsFolder
                )
            )
        );
    },
    dynamicContentTextClass: 'input-spaced'
};

export const preserveCompressionFileAsFolderMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'checkbox',
        bind: 'preserveCompressionFileNameAsFolder',
        label: stringResources.ModelName_PreserveCompressionFileNameAsFolder,
        defaultValue: true,
        description: stringResources.PreserveCompressionFileNameAsFolder_Description,
        labelPosition: 'left'
    },
    hintMessage: stringResources.PreserveCompressionFileNameAsFolder_Tip,
    hideHintMessage: (owner: TarGZipCompressionReadSettings | TarCompressionReadSettings) => {
        return (
            !owner?.owner?.owner?.owner ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !((owner.owner.owner.owner as any).sourceDataset instanceof FormatDatasetV2) ||
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            !(owner.owner.owner.owner as any).sourceDataset.location ||
            !(
                (
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (owner.owner.owner.owner as any).sourceDataset.location.isFolder() &&
                    owner.preserveCompressionFileNameAsFolder != null &&
                    !owner.preserveCompressionFileNameAsFolder
                )
            )
        );
    },
    dynamicContentTextClass: 'input-spaced'
};

export const useTempFileRenameCheckboxMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'checkbox',
        bind: 'useTempFileRename',
        label: stringResources.SFTP_UseTempFileRename_Label,
        defaultValue: true,
        description:
            stringResources.SFTP_UseTempFileRename_Desc +
            '<a target="_blank" href="https://aka.ms/sftp-sink' +
            stringResources.LearnMore +
            '</a>',
        escapeDescription: false,
        labelPosition: 'left'
    },
    dynamicContentTextClass: 'input-spaced'
};

export const wildcardFileNameMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'wildcardFileName',
        label: stringResources.ModelName_WildcardFileName
    },
    dynamicContentTextClass: 'input-spaced'
};

export const wildcardPathMetadata: ControlMetadata = {
    type: 'wildcard-path',
    bind: {
        ['wildcardFolderPath']: 'wildcardFolderPath',
        ['wildcardFileName']: 'wildcardFileName'
    },
    parameterizable: true
};

export const fileListPathMetadata: ControlMetadata = {
    type: 'filepath-path',
    bind: 'fileListPath',
    parameterizable: true,
    pathAttr: 'fileListPath',
    label: stringResources.ModelName_FileListPath,
    description: stringResources.FileListPathDescription
};

export const nativeFileFilterMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'fileFilter',
        label: stringResources.ModelName_FileFilter
    },
    dynamicContentTextClass: 'input-spaced'
};

export const listBeforeMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'listBefore',
        label: stringResources.ModelName_ListBefore,
        description: stringResources.ADLS_ListBefore_Description
    },
    dynamicContentTextClass: 'input-spaced'
};

export const listAfterMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'listAfter',
        label: stringResources.ModelName_ListAfter,
        description: stringResources.ADLS_ListAfter_Description
    },
    dynamicContentTextClass: 'input-spaced'
};

export const partitionDiscoveryMetadata: ControlMetadata = {
    type: 'partition-discovery',
    bind: {
        enablePartitionDiscovery: 'enablePartitionDiscovery',
        partitionRootPath: 'partitionRootPath'
    },
    parameterizable: true
};

export const modifiedDateTimeFilterMetadata: ControlMetadata = {
    type: 'modified-time-filter',
    bind: {
        ['modifiedDatetimeStart']: 'modifiedDatetimeStart',
        ['modifiedDatetimeEnd']: 'modifiedDatetimeEnd'
    },
    parameterizable: true
};

export const queryTimeoutMetadata: ControlMetadata = {
    type: 'parameterizable',
    label: stringResources.ModelName_Source_QueryTimeout,
    description: stringResources.QueryTimeout_DescriptionMessage,
    child: {
        type: 'textbox-number',
        bind: 'queryTimeout',
        label: stringResources.ModelName_Source_QueryTimeout_Min,
        description: stringResources.QueryTimeout_DescriptionMessage_short,
        min: 1,
        max: 1440,
        dataType: 'float',
        validators: [
            {
                type: 'number',
                min: 1,
                max: 1440,
                errorMessage: stringResources.QueryTimeout_ErrorMessage
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const queryTimeoutV2Metadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox-number',
        bind: 'queryTimeout',
        label: stringResources.ModelName_Source_QueryTimeout_Sec,
        description: stringResources.QueryTimeout_Sec_DescriptionMessage_short,
        inputType: 'number',
        min: 1,
        max: MAX_INT32,
        dataType: 'integer',
        validators: [
            {
                type: 'number',
                min: 1,
                max: MAX_INT32,
                errorMessage: format(stringResources.NumberRange_ErrorMessage, stringResources.ModelName_Source_QueryTimeout_Sec, '1', MAX_INT32.toString())
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const batchSizeV2Metadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox-number',
        bind: 'batchSize',
        label: stringResources.ModelName_BatchSize,
        inputType: 'number',
        min: 1,
        max: Number.MAX_SAFE_INTEGER,
        dataType: 'integer',
        validators: [
            {
                type: 'number',
                min: 1,
                max: Number.MAX_SAFE_INTEGER,
                errorMessage: format(stringResources.NumberRange_ErrorMessage, stringResources.ModelName_BatchSize, '1', Number.MAX_SAFE_INTEGER.toString())
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const httpRequestTimeoutMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'httpRequestTimeout',
        label: `${stringResources.ModelName_Request_Timeout}`,
        description: stringResources.HttpRequestTimeout_DescriptionMessage,
        validators: [
            {
                type: 'pattern',
                pattern: Constants.timeoutValidationPattern,
                errorMessage: format(stringResources.InvalidTimeoutPattern, stringResources.ModelName_Request_Timeout)
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const copyBehaviorMetadata: ControlMetadata = {
    type: 'parameterizable-dropdown',
    bind: 'copyBehavior',
    label: stringResources.ModelName_CopyBehavior,
    description: stringResources.CopyBehavior_Description,
    options: copyBehaviorOptions,
    allowClear: true,
    placeholderText: stringResources.Select
};

export const operationTimeoutMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox-number',
        bind: 'operationTimeout',
        label: stringResources.ModelName_OperationTimeout,
        description: stringResources.OperationTimeout_Description
    },
    dynamicContentTextClass: 'input-spaced'
};

export const binaryCopyBehaviorMetadata: ControlMetadata = {
    type: 'parameterizable-dropdown',
    bind: 'copyBehavior',
    label: stringResources.ModelName_CopyBehavior,
    description: stringResources.CopyBehavior_Description,
    options: copyBehaviorOptions.filter((op) => op.value !== copyBehaviorOptionValues.mergeFiles),
    allowClear: true,
    placeholderText: stringResources.Select
};

export const hdfsDistCpMetadata: ControlMetadata = {
    type: 'hdfs-distcp-setting',
    bind: 'distCpSetting'
};

export const quoteAllTextMetadata: ControlMetadata = {
    type: 'checkbox',
    bind: 'quoteAllText',
    label: stringResources.ModelName_QuoteAllText,
    labelPosition: 'left',
    disabled: true
};

export const fileExtensionMetadata: ControlMetadata = {
    type: 'file-extension',
    bind: 'fileExtension'
};

export const maxRowsPerFileAndPrefixMetadata: ControlMetadata = {
    type: 'max-rows-filename-prefix',
    bind: {
        maxRowsPerFileComp: 'maxRowsPerFile',
        fileNamePrefixComp: 'fileNamePrefix'
    }
};

export const fileNamePrefixMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'fileNamePrefix',
        label: `${stringResources.FileNameLabel} ${stringResources.Prefix.toLocaleLowerCase()}`
    },
    dynamicContentTextClass: 'input-spaced'
};

export const expiryDatetimeStartMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'date-time-picker',
        bind: 'expiryDatetime',
        label: stringResources.ModelName_ExpiryDatetime,
        drops: DirectionalHint.topLeftEdge,
        styleOverrides: {
            width: '452px'
        },
        description: stringResources.ModelName_ExpiryDatetime_Description
    },
    dynamicContentTextClass: 'input-spaced'
};

export function getFilePathSelectionMetadata(
    withPrefix?: boolean,
    withFileFilter?: boolean,
    notWithTimeFilter?: boolean,
    withListBeforeAndAfter?: boolean
): ControlMetadata {
    return {
        type: 'radio-switch',
        label: stringResources.FilePathType,
        bind: 'filePathMode',
        nested: 1,
        options: [
            {
                label: __IS_FABRIC_DPE__ ? stringResources.FilePath_Label : stringResources.DefaultDatasetFilePath,
                value: FilePathMode.DATASET
            },
            {
                label: stringResources.ModelName_FileFilter,
                value: FilePathMode.FILEFILTER,
                hidden: !withFileFilter
            },
            {
                label: stringResources.Prefix,
                value: FilePathMode.PREFIX,
                hidden: !withPrefix
            },
            {
                label: stringResources.ModelName_ListBeofreAndAfterMode,
                value: FilePathMode.NameRange,
                hidden: !withListBeforeAndAfter
            },
            {
                label: stringResources.WildcardPath_Label,
                value: FilePathMode.WILDCARD
            },
            {
                label: stringResources.DataFlowSourceSettingsListOfFiles,
                value: FilePathMode.LIST,
                description: stringResources.copyFileListPathDescription
            }
        ],
        defaultValue: FilePathMode.DATASET,
        cases: [
            {
                caseValue: FilePathMode.DATASET,
                children: []
            },
            {
                caseValue: FilePathMode.FILEFILTER,
                children: [nativeFileFilterMetadata]
            },
            {
                caseValue: FilePathMode.PREFIX,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'prefix',
                            label: stringResources.Prefix
                        },
                        dynamicContentTextClass: 'input-spaced'
                    }
                ]
            },
            {
                caseValue: FilePathMode.NameRange,
                children: [listAfterMetadata, listBeforeMetadata]
            },
            {
                caseValue: FilePathMode.WILDCARD,
                children: [wildcardPathMetadata]
            },
            {
                caseValue: FilePathMode.LIST,
                children: [fileListPathMetadata]
            }
        ],
        sharedControls: [
            {
                caseValues: [
                    FilePathMode.DATASET,
                    FilePathMode.FILEFILTER,
                    FilePathMode.WILDCARD,
                    FilePathMode.PREFIX,
                    FilePathMode.NameRange
                ],
                controls: notWithTimeFilter
                    ? [recursiveCheckboxMetadata]
                    : [modifiedDateTimeFilterMetadata, recursiveCheckboxMetadata]
            }
        ]
    };
}

export function disableChunkingMetadata(infoLink: string): ControlMetadata {
    return {
        type: 'parameterizable',
        child: {
            type: 'checkbox',
            bind: 'disableChunking',
            label: stringResources.ModelName_DisableChunking,
            defaultValue: false,
            description:
                stringResources.SFTP_DisableChunking_Desc +
                ' <a target="_blank" href="' +
                infoLink +
                '">' +
                stringResources.LearnMore +
                '</a>',
            escapeDescription: false,
            labelPosition: 'left'
        },
        dynamicContentTextClass: 'input-spaced'
    };
}

// we will merge getCommonFileTypeStoreReadSettingControls and getCommonBinaryStoreReadSettingControls after all controls unify
export function getCommonFileTypeStoreReadSettingControls(
    withPrefix?: boolean,
    withFileFilter?: boolean,
    notWithTimeFilter?: boolean,
    notWithPartition?: boolean,
    withListBeforeAndAfter?: boolean,
    disableChunkingSettings?: IDisableChunkingSettings
): ControlMetadata[] {
    const controls: ControlMetadata[] = [
        getFilePathSelectionMetadata(withPrefix, withFileFilter, notWithTimeFilter, withListBeforeAndAfter)
    ];
    if (disableChunkingSettings != null) {
        controls.push(disableChunkingMetadata(disableChunkingSettings.infoLink));
    }
    if (!notWithPartition) {
        controls.push(partitionDiscoveryMetadata);
    }
    controls.push(maxConcurrentConnectionsMetadata);
    return controls;
}

export function getCommonBinaryStoreReadSettingControls(
    withPrefix?: boolean,
    withFileFilter?: boolean,
    notWithTimeFilter?: boolean,
    withListBeforeAndAfter?: boolean,
    withDeleteFileAfterCompletion: boolean = true,
    disableChunkingSettings?: IDisableChunkingSettings
): ControlMetadata[] {
    const controls: ControlMetadata[] = [
        getFilePathSelectionMetadata(withPrefix, withFileFilter, notWithTimeFilter, withListBeforeAndAfter)
    ];
    if (disableChunkingSettings != null) {
        controls.push(disableChunkingMetadata(disableChunkingSettings.infoLink));
    }
    if (withDeleteFileAfterCompletion) {
        controls.push(deleteFilesAfterCompletionMetadata);
    }
    controls.push(maxConcurrentConnectionsMetadata);
    return controls;
}

export function getCommonDeltaStoreReadSettingControls(): ControlMetadata[] {
    return [maxConcurrentConnectionsMetadata];
}

export const commonFileTypeStoreWriteSettingControls: ControlMetadata[] = [
    copyBehaviorMetadata,
    maxConcurrentConnectionsMetadata
];

export const commonBinaryStoreWriteSettingControls: ControlMetadata[] = [
    binaryCopyBehaviorMetadata,
    maxConcurrentConnectionsMetadata
];

export const commonDeltaStoreWriteSettingControls: ControlMetadata[] = [maxConcurrentConnectionsMetadata];

export const getTableActions = (showUpsert: boolean, hide?: string): ControlMetadata[] => {
    return [
        {
            type: 'radio-switch',
            hide: `{ ${hide} || !isOldResource}`,
            label: stringResources.TableAction,
            bind: 'tableActionOption',
            isParameterizable: true,
            outsideAdvancedSectionInFabric: true,
            options: getLakehouseTableActionOptions(showUpsert, true),
            defaultValue: TableActionOption.Append,
            cases: [
                {
                    caseValue: TableActionOption.Upsert,
                    children: [
                        {
                            type: 'sql-upsert',
                            bind: {
                                keys: 'keyColumns'
                            },
                            hasTempDB: false,
                            hasInterimSchemaName: false,
                            keyColumnsAttributeName: 'keyColumns',
                            parameterizable: true,
                            required: true
                        }
                    ]
                }
            ]
        },
        {
            type: 'radio-switch',
            hide: `{ ${hide} || isOldResource }`,
            label: stringResources.TableAction,
            bind: 'tableActionOption',
            isParameterizable: true,
            outsideAdvancedSectionInFabric: true,
            options: getLakehouseTableActionOptions(showUpsert, false),
            defaultValue: TableActionOption.Append,
            cases: [
                {
                    caseValue: TableActionOption.Upsert,
                    children: [
                        {
                            type: 'sql-upsert',
                            bind: {
                                keys: 'keyColumns'
                            },
                            hasTempDB: false,
                            hasInterimSchemaName: false,
                            keyColumnsAttributeName: 'keyColumns',
                            parameterizable: true,
                            required: true
                        }
                    ]
                }
            ]
        },
        {
            type: 'checkbox-switch',
            bind: 'enablePartitionOption',
            hide: `{ tableActionOption !== 'OverwriteSchema' && tableActionOption !== 'Overwrite' }`,
            showDynamically: true,
            isParameterizable: true,
            label: stringResources.ModelName_Enable_Sink_Partition,
            description: stringResources.CopySinkEnablePatitionOptionDescription,
            parameterizedLabel: stringResources.ModelName_Partition_Option,
            defaultValue: false,
            labelPosition: 'left',
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'store-setting-partition-keys-order',
                            bind: 'partitionNameList',
                            description: stringResources.CopySinkPatitionColumnNamesDescription,
                            actionType: 'Overwrite'
                        }
                    ]
                }
            ]
        }
    ];
};

export const getStoreWriteSettingControls = (
    showUpsert: boolean,
    blockSizeInMbDesc: string,
    fileType: string = ''
): ControlMetadata[] => {
    const hideCopyBehavior = __IS_FABRIC_DPE__
        ? '{ owner.owner.sinkDataset.rootFolder === "Tables" }'
        : '{ owner.owner.sinkDataset.linkedService.rootFolder === "Tables" }';
    if (fileType === ModelConstants.jsonDataset) {
        return [
            {
                ...copyBehaviorMetadata,
                hide: hideCopyBehavior
            },
            maxConcurrentConnectionsMetadata,
            blockSizeInMbMetadata(blockSizeInMbDesc),
            {
                type: 'store-setting-metadata',
                bind: 'metadata',
                parameterizable: true
            }
        ];
    }

    return [
        ...getTableActions(
            showUpsert,
            __IS_FABRIC_DPE__
                ? 'owner.owner.sinkDataset.rootFolder !== "Tables"'
                : 'owner.owner.sinkDataset.linkedService.rootFolder !== "Tables"'
        ),
        {
            ...copyBehaviorMetadata,
            hide: hideCopyBehavior
        },
        maxConcurrentConnectionsMetadata,
        {
            type: 'store-setting-metadata',
            bind: 'metadata',
            parameterizable: true
        }
    ];
};

export const getBinaryStoreWrittingControls = (blockSizeInMbDesc: string): ControlMetadata[] => {
    return [
        ...commonBinaryStoreWriteSettingControls,
        blockSizeInMbMetadata(blockSizeInMbDesc),
        {
            type: 'store-setting-metadata',
            bind: 'metadata',
            options: [
                {
                    label: '$$LASTMODIFIED',
                    value: '$$LASTMODIFIED',
                    tooltip: stringResources.Metadata_LastModified_Desc
                }
            ],
            parameterizable: true
        }
    ];
};

export const commonFileTypeStoreGetMetadataActivityReadSettings: string[] = [
    'modifiedDatetimeStart',
    'modifiedDatetimeEnd'
];

export const commonFileTypeStoreDeleteActivityReadSettings: string[] = [
    'wildcardFileName',
    'modifiedDatetimeStart',
    'modifiedDatetimeEnd',
    'recursive',
    'maxConcurrentConnections'
];

export const cassandraConsistencyLevelOptions: Array<ISelectOption<string>> = [
    {
        label: 'ONE',
        value: 'one'
    },
    {
        label: 'TWO',
        value: 'two'
    },
    {
        label: 'THREE',
        value: 'three'
    },
    {
        label: 'QUORUM',
        value: 'quorum'
    },
    {
        label: 'ALL',
        value: 'all'
    },
    {
        label: 'LOCAL_QUORUM',
        value: 'local_quorum'
    },
    {
        label: 'EACH_QUORUM',
        value: 'each_quorum'
    },
    {
        label: 'LOCAL_ONE',
        value: 'local_one'
    }
];

export const requestMethodOptions: Array<ISelectOption<string | undefined>> = [
    {
        label: stringResources.Dataset_RequestMethod_Get,
        value: 'GET'
    },
    {
        label: stringResources.Method_Post,
        value: 'POST'
    }
];

export const office365UserScopes: { all: 'all'; group: 'group' } = {
    all: 'all',
    group: 'group'
};

type Office365UserScope = (typeof office365UserScopes)[keyof typeof office365UserScopes];

export const office365UserScopeOptions: Array<ISelectOption<Office365UserScope>> = [
    {
        label: stringResources.Office365_UserScope_SelectAll_Label,
        value: office365UserScopes.all
    },
    {
        label: stringResources.Office365_UserScope_SelectGroups_Label,
        value: office365UserScopes.group
    }
];

export const office365UnknownScopeOptions: Array<ISelectOption<Office365UserScope>> = [
    {
        label: stringResources.Office365_UserGroupScope_SelectAll_Label,
        value: office365UserScopes.all
    },
    {
        label: stringResources.Office365_UserScope_SelectGroups_Label,
        value: office365UserScopes.group
    }
];

export const office365GroupScopeOptions: Array<ISelectOption<Office365UserScope>> = [
    {
        label: stringResources.Office365_GroupScope_SelectAll_Label,
        value: office365UserScopes.all
    },
    {
        label: stringResources.Office365_UserScope_SelectGroups_Label,
        value: office365UserScopes.group
    }
];

export const completedDateTimeStartMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'date-time-picker',
        bind: 'completedDateTimeStart',
        label: stringResources.ModelName_CompletedDateTimeStart,
        drops: DirectionalHint.topLeftEdge,
        required: true,
        description: stringResources.AdobeExperiencePlatfor_CompletedDateTimeDescription,
        styleOverrides: { width: '450px' }
    },
    dynamicContentTextClass: 'input-spaced'
};

export const completedDateTimeEndMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'date-time-picker',
        bind: 'completedDateTimeEnd',
        label: stringResources.ModelName_CompletedDateTimeEnd,
        drops: DirectionalHint.topLeftEdge,
        required: true,
        description: stringResources.AdobeExperiencePlatfor_CompletedDateTimeDescription,
        styleOverrides: { width: '450px' }
    },
    dynamicContentTextClass: 'input-spaced'
};

export const disableMetricsCollectionCheckboxMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'checkbox',
        bind: 'disableMetricsCollection',
        label: stringResources.CopySinkDisableMetricsCollectionLabel,
        description: stringResources.CopySinkDisableMetricsCollectionDescription,
        labelPosition: 'left'
    },
    dynamicContentTextClass: 'input-spaced'
};

export const isolationLevelMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'select',
        bind: 'isolationLevel',
        label: stringResources.DataFlowSQLIsolationLevel,
        description:
            stringResources.ModelName_IsolationLevelDescription +
            '<a target="_blank" href="https://aka.ms/sqlsource-isolation-level">' +
            stringResources.LearnMore +
            '</a>',
        escapeDescription: false,
        descriptionLink: 'https://aka.ms/sqlsource-isolation-level',
        allowClear: true,
        placeholderText: stringResources.Select,
        options: [
            {
                label: stringResources.DataFlowSQLIsolationLevelReadCommitted,
                value: isolationLevelConst.ReadCommitted
            },
            {
                label: stringResources.DataFlowSQLIsolationLevelReadUncommitted,
                value: isolationLevelConst.ReadUncommitted
            },
            {
                label: stringResources.DataFlowSQLIsolationLevelRepeatableRead,
                value: isolationLevelConst.RepeatableRead
            },
            {
                label: stringResources.DataFlowSQLIsolationLevelSerializable,
                value: isolationLevelConst.Serializable
            },
            {
                label: stringResources.ModelName_Snapshot,
                value: isolationLevelConst.Snapshot
            }
        ],
        labelPosition: 'left'
    },
    dynamicContentTextClass: 'input-spaced'
};

export const azureCloudTypeMetadata: ControlMetadata = {
    type: 'select',
    bind: 'azureCloudType',
    label: shellStringResources.ModelName_AzureCloudType,
    description: formatStringForAppType(stringResources.AzureCloudType_Description),
    options: [
        {
            label: formatStringForAppType(stringResources.AzureCloudType_Auto),
            value: undefined
        },
        {
            label: stringResources.AzurePublicCloudLabel,
            value: azureCloudTypeConst.AzurePublic
        },
        {
            label: stringResources.AzureChinaCloudLabel,
            value: azureCloudTypeConst.AzureChina
        },
        {
            label: stringResources.AzureUSGovernmentCloudLabel,
            value: azureCloudTypeConst.AzureUSGovernment
        },
        {
            label: stringResources.AzureGermanyCloudLabel,
            value: azureCloudTypeConst.AzureGermany
        }
    ],
    isLabelVertical: true
};

export const parameterizableAzureCloudTypeMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'select',
        bind: 'azureCloudType',
        label: shellStringResources.ModelName_AzureCloudType,
        description: formatStringForAppType(stringResources.AzureCloudType_Description),
        options: [...azureCloudTypeMetadata.options],
        isLabelVertical: true
    }
};

export const sapColumnDelimiterMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'select',
        bind: 'sapDataColumnDelimiter',
        editable: true,
        options: columnDelimiterWithoutNoneV2Options,
        label: stringResources.ModelName_ColumnDelimiter,
        description: stringResources.Sap_ColumnDelimiter_Description
    },
    dynamicContentTextClass: 'input-spaced'
};

export const partitionOptionForwardLinkMap: { [key: string]: string } = {
    [adfModelTypeMap.oracle.copySourceType]: 'https://aka.ms/oraclepartitionoption',
    [adfModelTypeMap.teradata.copySourceType]: 'https://aka.ms/teradatacopysource',
    [adfModelTypeMap.sapOpenHub.copySourceType]: 'https://aka.ms/sapopenhubcopysource',
    [adfModelTypeMap.netezza.copySourceType]: 'https://aka.ms/netezzaconnector',
    [adfModelTypeMap.sapTable.copySourceType]: 'https://aka.ms/saptablesource',
    [adfModelTypeMap.sapHana.copySourceType]: 'https://go.microsoft.com/fwlink/?linkid=2112256',
    [adfModelTypeMap.azureSql.copySourceType]: 'https://aka.ms/azuresqlconnector',
    [adfModelTypeMap.azureSqlDW.copySourceType]: 'https://aka.ms/sqldwconnector',
    [adfModelTypeMap.azureSqlMI.copySourceType]: 'https://aka.ms/sqlmiconector',
    [adfModelTypeMap.sqlServer.copySourceType]: 'https://aka.ms/sqlserverconnector',
    [adfModelTypeMap.azurePostgreSQL.copySourceType]: 'https://aka.ms/AAd4rwd',
    [adfModelTypeMap.amazonRdsForOracle.copySourceType]: 'https://aka.ms/amazonrdsfororaclepartitionoption',
    [adfModelTypeMap.amazonRdsSqlServer.copySourceType]: 'https://aka.ms/amazonrdsforsqlserverpartitionoption',
    [adfModelTypeMap.fabricSql.copySourceType]: 'https://aka.ms/fabricsqlconnector',
    [adfModelTypeMap.lakehouseTable.copySourceType]: 'https://go.microsoft.com/fwlink/?linkid=2333375'
};

const sharedDynamicsOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: dynamicsAuthTypes.AADServicePrincipal
    },
    {
        label: stringResources.DynamicsAuthTypeOffice365,
        value: dynamicsAuthTypes.Office365
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: dynamicsAuthTypes.ManagedIdentity
    }
];

export const parameterizeDynamicsControlMetadata = (controlMetaData: ControlMetadata): ControlMetadata => {
    if (controlMetaData.type === 'dynamic-akv-switcher') {
        const { bind, akvOnly, child } = controlMetaData;
        return !!child && !akvOnly
            ? {
                ...controlMetaData,
                enableAKVParameterization: true,
                child: {
                    attribute: bind,
                    disableAddParameter: true,
                    ...parameterizeDynamicsControlMetadata(controlMetaData.child)
                }
            }
            : { ...controlMetaData, enableAKVParameterization: true };
    }
    if (controlMetaData.type === 'select') {
        return controlMetaData;
    }
    return {
        type: 'parameterizable',
        child: controlMetaData
    };
};

const dynamicsServiceUriControlMetadata: ControlMetadata = {
    type: 'textbox',
    bind: 'serviceUri',
    label: stringResources.ModelName_ServiceUri,
    isLabelVertical: true,
    placeholder: stringResources.DynamicsServiceUriPlaceholder,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }]
};
const dynamicsServicePrincipalIdControlMetadata: ControlMetadata = {
    type: 'textbox',
    bind: 'servicePrincipalId',
    label: stringResources.ModelName_ServicePrincipalID,
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true
};
const dynamicsSPKControlMetadata: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'servicePrincipalKey',
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'password',
        label: stringResources.ModelName_ServicePrincipalKey,
        required: true,
        isLabelVertical: true
    },
    validators: [{ type: 'required', errorMessage: '' }],
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
        stringResources.ModelName_ServicePrincipalKey
    )
};

const dynamicsAuthTypeOffice365ControlMetadatas: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'username',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true
        },
        validators: [{ type: 'required', errorMessage: '' }],
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
            stringResources.ModelName_Password
        )
    }
];

const commonDynamicsDeployTypesOnPremControlMetadata: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'username',
        label: stringResources.ModelName_UserName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            required: true,
            isLabelVertical: true
        },
        validators: [{ type: 'required', errorMessage: '' }],
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
            stringResources.ModelName_Password
        )
    }
];

const dynamicsDeployTypesOnPremiseControlMetadatas: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'organizationName',
        label: stringResources.ModelName_OrganizationName,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'hostName',
        label: stringResources.ModelName_Host,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'textbox',
        bind: 'port',
        label: stringResources.ModelName_Port,
        isLabelVertical: true,
        inputType: 'number',
        defaultValue: 443,
        min: 0
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        isLabelVertical: true,
        options: [
            {
                label: `${stringResources.DynamicsAuthTypeAD} (${stringResources.Recommended})`,
                value: dynamicsAuthTypes.ADAuth
            },
            {
                label: `${stringResources.DynamicsAuthTypeIfd} ${stringResources.LegacyName}`,
                value: dynamicsAuthTypes.Ifd
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        defaultValue: dynamicsAuthTypes.ADAuth,
        cases: [
            {
                caseValue: dynamicsAuthTypes.Ifd,
                children: commonDynamicsDeployTypesOnPremControlMetadata.map(parameterizeDynamicsControlMetadata)
            },
            {
                caseValue: dynamicsAuthTypes.ADAuth,
                children: [
                    {
                        type: 'textbox',
                        bind: 'domain',
                        label: stringResources.DomainLabel,
                        description: stringResources.DynamicsADDomainDesc,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    },
                    ...commonDynamicsDeployTypesOnPremControlMetadata
                ].map(parameterizeDynamicsControlMetadata)
            }
        ],
        warningMessageCalculator: (value: string) => value === dynamicsAuthTypes.Ifd ? stringResources.DynamicsLegacySupport : '',
    }
];

const dynamicsServicePrincipalCertControlMetadatas: ControlMetadata[] = [
    {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'servicePrincipalCert',
        label: stringResources.ModelName_ServicePrincipalCert,
        validators: [{ type: 'required', errorMessage: '' }],
        akvOnly: true,
        isCert: true,
        akvOptionDisplayText: stringResources.AzureKeyVault_ServicePrincipalCert_DisplayText,
        description: format(
            stringResources.AzureKeyVaultCertName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
            stringResources.ModelName_ServicePrincipalCert
        )
    }
];

export const oracleV2NumberSettingsMetadata: ControlMetadata = {
    type: 'accordion',
    bind: 'oracleNumberSettings',
    header: stringResources.Oracle_NumberSettings,
    description: stringResources.Oracle_NumberSettings_Description,
    emitValueChange: false,
    hide: `{ !owner.sourceDataset.linkedService || (owner.sourceDataset.linkedService.showConvertDecimalToIntegerControl && !owner.sourceDataset.linkedService.supportV1DataTypesIsParameterizable) }`,
    children: [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'numberPrecision',
                label: stringResources.DataFlowColumnType_DecimalPrecision,
                description: stringResources.Oracle_Precision_Description,
                placeholder: '1-256',
                inputType: 'number',
                validators: [
                    {
                        type: 'number',
                        min: 1,
                        max: 256,
                        errorMessage: stringResources.Oracle_Precision_ErrorMessage
                    }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'numberScale',
                label: stringResources.DataFlowColumnType_Scale,
                description: stringResources.Oracle_Scale_Description,
                placeholder: '0-130',
                inputType: 'number',
                validators: [
                    {
                        type: 'number',
                        min: 0,
                        max: 130,
                        errorMessage: stringResources.Oracle_Scale_ErrorMessage
                    }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        }
    ]
};

export const sharedDynamicsMetadata: ControlMetadata[] = [
    {
        type: 'radio-switch',
        bind: 'deploymentType',
        label: stringResources.ModelName_DeploymentType,
        options: [
            {
                label: stringResources.DynamicsDeploymentTypeOnline,
                value: dynamicsDeploymentTypes.Online
            },
            {
                label: stringResources.DynamicsDeploymentTypeOnPremisesWithIfd,
                value: dynamicsDeploymentTypes.OnPremisesWithIfd
            }
        ],
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        defaultValue: dynamicsDeploymentTypes.Online,
        cases: [
            {
                caseValue: dynamicsDeploymentTypes.Online,
                children: [
                    parameterizeDynamicsControlMetadata(dynamicsServiceUriControlMetadata),
                    {
                        type: 'select-switch',
                        bind: 'authenticationType',
                        label: stringResources.ModelName_AuthenticationType,
                        description: stringResources.DynamicAuthenticationTypeTooltip,
                        isLabelVertical: true,
                        options: mergeUserAuthCredentialOption(sharedDynamicsOptions, dynamicsAuthTypes.UserAuth),
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        defaultValue: dynamicsAuthTypes.AADServicePrincipal,
                        cases: [
                            {
                                caseValue: dynamicsAuthTypes.AADServicePrincipal,
                                children: [
                                    {
                                        type: 'select-switch',
                                        bind: 'servicePrincipalCredentialType',
                                        label: stringResources.ModelName_ServicePrincipalCredentialType,
                                        isLabelVertical: true,
                                        options: [
                                            {
                                                label: stringResources.ModelName_ServicePrincipalKey,
                                                value: servicePrincipalCredentialTypes.ServicePrincipalKey
                                            },
                                            {
                                                label: stringResources.ModelName_ServicePrincipalCert,
                                                value: servicePrincipalCredentialTypes.ServicePrincipalCert
                                            }
                                        ],
                                        required: true,
                                        validators: [{ type: 'required', errorMessage: '' }],
                                        defaultValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
                                        cases: [
                                            {
                                                caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
                                                children: [
                                                    parameterizeDynamicsControlMetadata(
                                                        dynamicsServicePrincipalIdControlMetadata
                                                    ),
                                                    parameterizeDynamicsControlMetadata(dynamicsSPKControlMetadata)
                                                ]
                                            },
                                            {
                                                caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
                                                children: dynamicsServicePrincipalCertControlMetadatas.map(
                                                    parameterizeDynamicsControlMetadata
                                                )
                                            }
                                        ]
                                    }
                                ]
                            },
                            {
                                caseValue: dynamicsAuthTypes.Office365,
                                children: dynamicsAuthTypeOffice365ControlMetadatas.map(
                                    parameterizeDynamicsControlMetadata
                                )
                            },
                            {
                                caseValue: dynamicsAuthTypes.ManagedIdentity,
                                children: [
                                    {
                                        type: 'credential-select',
                                        bind: 'credential',
                                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                        required: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                ]
                            },
                            {
                                caseValue: dynamicsAuthTypes.UserAuth,
                                children: [
                                    {
                                        type: 'credential-select',
                                        hide: !GlobalVariables.enableConnectorUserAuthCred,
                                        bind: 'credential',
                                        filterCredentialType: CredentialTypeOptions.UserAuth,
                                        required: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                caseValue: dynamicsDeploymentTypes.OnPremisesWithIfd,
                children: dynamicsDeployTypesOnPremiseControlMetadatas.map(parameterizeDynamicsControlMetadata)
            }
        ]
    }
];

export function getSqlParallelReadMetadataConsts(sourceType: string): ControlMetadata {
    return {
        type: 'radio-switch',
        label: stringResources.ModelName_Partition_Option,
        defaultValue: partitionOptionConst.none,
        description:
            '<a target="_blank" href="' +
            partitionOptionForwardLinkMap[sourceType] +
            '">' +
            stringResources.LearnMore +
            '</a>',
        escapeDescription: false,
        descriptionLink: partitionOptionForwardLinkMap[sourceType],
        options: sqlSourcePartitionOptions,
        bind: 'partitionOption',
        isParameterizable: true,
        inlineHintMessage: stringResources.Sql_Partition_Info,
        cases: [
            {
                caseValue: partitionOptionConst.dyamicRange,
                children: [...dynamicRangePartitionControls]
            }
        ]
    };
}

export const dynamicsAlternateKeyMetadata: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'alternate-key-selection',
        bind: 'alternateKeyName',
        label: stringResources.ModelName_AlternateKeyName,
        description: stringResources.DynamicsAlternateKey_Description
    },
    dynamicContentTextClass: 'input-spaced'
};

export enum AzureBlobStorageReadsettingFilepathMode {
    DATASET,
    WILDCARD,
    PREFIX
}

export function getEncryptionAuthTypeToResource(authType: AuthenticationType): string {
    switch (authType) {
        case AuthenticationType.ServicePrincipal:
            return 'ServicePrincipal';
        case AuthenticationType.ManagedServiceIdentity:
            return 'ManagedIdentity';
        case AuthenticationType.UserAssignedManagedIdentity:
            return 'UserAssignedManagedIdentity';
        case AuthenticationType.UserAuth:
            return AuthenticationType.UserAuth;
        default:
            return '';
    }
}

export function getEncryptionAuthTypeToModel(authType: string): AuthenticationType | undefined {
    switch (authType) {
        case 'ServicePrincipal':
            return AuthenticationType.ServicePrincipal;
        case 'ManagedIdentity':
            return AuthenticationType.ManagedServiceIdentity;
        case 'UserAssignedManagedIdentity':
            return AuthenticationType.UserAssignedManagedIdentity;
        case AuthenticationType.UserAuth:
            return AuthenticationType.UserAuth;
        default:
            return undefined;
    }
}

export function sqlEncryption(sqlType: string): ControlMetadata {
    return {
        type: 'checkbox-switch',
        bind: 'alwaysEncrypted',
        label: stringResources.AlwaysEncrypted,
        description: format(stringResources.AlwaysEncryptedDesc, sqlType),
        defaultValue: false,
        labelPosition: 'left',
        nested: 1,
        cases: [
            {
                caseValue: true,
                children: [
                    {
                        type: 'select-switch',
                        bind: 'keyStoreAuthType',
                        label: stringResources.KeyStoreAuthType,
                        options: mergeUserAuthCredentialOption(
                            [
                                {
                                    label: stringResources.AuthenticationType_ManagedServiceIdentity,
                                    value: AuthenticationType.ManagedServiceIdentity
                                },
                                {
                                    label: stringResources.AuthenticationType_ServicePrincipal,
                                    value: AuthenticationType.ServicePrincipal
                                },
                                {
                                    label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
                                    value: AuthenticationType.UserAssignedManagedIdentity
                                }
                            ],
                            AuthenticationType.UserAuth
                        ),
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true,
                        defaultValue: AuthenticationType.ManagedServiceIdentity,
                        cases: [
                            {
                                caseValue: AuthenticationType.ServicePrincipal,
                                children: [
                                    {
                                        type: 'parameterizable',
                                        child: {
                                            type: 'textbox',
                                            bind: 'encryptionServicePrincipalId',
                                            label: stringResources.ModelName_ServicePrincipalID,
                                            required: true,
                                            validators: [{ type: 'required', errorMessage: '' }],
                                            isLabelVertical: true,
                                            description:
                                                '<a target="_blank" href="https://learn.microsoft.com/azure/data-factory/connector-azure-sql-database?tabs=data-factory#service-principal-authentication">' +
                                                stringResources.LearnMore +
                                                '</a>',
                                            escapeDescription: false
                                        }
                                    },
                                    {
                                        type: 'dynamic-akv-switcher',
                                        enableAKVParameterization: true,
                                        bind: 'encryptionServicePrincipalKey',
                                        label: stringResources.ModelName_ServicePrincipalKey,
                                        child: {
                                            type: 'parameterizable',
                                            attribute: 'encryptionServicePrincipalKey',
                                            child: {
                                                type: 'password',
                                                label: stringResources.ModelName_ServicePrincipalKey,
                                                required: true,
                                                isLabelVertical: true,
                                                validators: [{ type: 'required', errorMessage: '' }]
                                            }
                                        },
                                        description: format(
                                            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                            format(
                                                stringResources.ModelName_LinkedServiceTemplate,
                                                stringResources.AzureSql_DisplayText
                                            ),
                                            stringResources.ModelName_ServicePrincipalKey
                                        )
                                    }
                                ]
                            },
                            {
                                caseValue: AuthenticationType.ManagedServiceIdentity,
                                children: [
                                    {
                                        type: 'management-identity-info',
                                        resourceType: stringResources.AzureSql_DisplayText,
                                        link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-database?tabs=data-factory#managed-identity'
                                    }
                                ]
                            },
                            {
                                caseValue: AuthenticationType.UserAssignedManagedIdentity,
                                children: [
                                    {
                                        type: 'credential-select',
                                        bind: 'alwaysEncryptionCredential',
                                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                        required: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                ]
                            },
                            {
                                caseValue: AuthenticationType.UserAuth,
                                children: [
                                    {
                                        type: 'credential-select',
                                        bind: 'alwaysEncryptionCredential',
                                        hide: !GlobalVariables.enableConnectorUserAuthCred,
                                        filterCredentialType: CredentialTypeOptions.UserAuth,
                                        required: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    };
}

export const sqlEncryptControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'select',
        bind: 'encrypt',
        defaultValue: 'mandatory',
        label: stringResources.ModelName_Encrypt,
        placeholderText: stringResources.Select,
        description: stringResources.Sql_Encrypt_Info,
        options: [
            {
                label: stringResources.Mandatory,
                value: 'mandatory'
            },
            {
                label: stringResources.Optional,
                value: 'optional'
            },
            {
                label: stringResources.Strict,
                value: 'strict'
            }
        ],
        isLabelVertical: true
    }
};

export const sqlHostNameInCertificate: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'hostNameInCertificate',
        label: stringResources.ModelName_HostNameInCertificate,
        isLabelVertical: true
    }
}


export const sqlTrustServerCertificateControl: ControlMetadata = {
    type: 'checkbox-switch',
    bind: 'trustServerCertificate',
    isParameterizable: true,
    label: stringResources.ModelName_TrustServerCertificate,
    description: stringResources.Sql_Trust_Server_Certificate_Info,
    parameterizedLabel: stringResources.ModelName_TrustServerCertificate,
    defaultValue: false,
    labelPosition: 'left',
    cases: [
        {
            caseValue: false,
            children: [
                sqlHostNameInCertificate
            ]
        }
    ],
    isLabelVertical: true
};

export function connectionStringParseControl(additionalFieldsParameterizable: boolean = false): ControlMetadata {
    return {
        type: 'ls-connectionstring-parse-button',
        additionalFieldsParameterizable,
        hide: '{ extension._state$ == undefined || extension._state$.state !== 1 }'
    };
}

export const sqlAdditionalPropertiesKeyOptions: Array<ISelectOption<SqlAdditionalPropertiesKeyType>> = [
    {
        label: 'Application intent',
        value: 'applicationIntent',
        type: Type.String
    },
    {
        label: 'Connect timeout',
        value: 'connectTimeout',
        type: Type.Int
    },
    {
        label: 'Connect retry count',
        value: 'connectRetryCount',
        type: Type.Int
    },
    {
        label: 'Connect retry interval',
        value: 'connectRetryInterval',
        type: Type.Int
    },
    {
        label: 'Load balance timeout',
        value: 'loadBalanceTimeout',
        type: Type.Int
    },
    {
        label: 'Command timeout',
        value: 'commandTimeout',
        type: Type.Int
    },
    {
        label: 'Integrated security',
        value: 'integratedSecurity',
        type: Type.Boolean
    },
    {
        label: 'Failover partner',
        value: 'failoverPartner',
        type: Type.String
    },
    {
        label: 'Max pool size',
        value: 'maxPoolSize',
        type: Type.Int
    },
    {
        label: 'Min pool size',
        value: 'minPoolSize',
        type: Type.Int
    },
    {
        label: 'Multiple active result sets',
        value: 'multipleActiveResultSets',
        type: Type.Boolean
    },
    {
        label: 'Multi subnet failover',
        value: 'multiSubnetFailover',
        type: Type.Boolean
    },
    {
        label: 'Packet size',
        value: 'packetSize',
        type: Type.Int
    },
    {
        label: 'Pooling',
        value: 'pooling',
        type: Type.Boolean
    }
];

export const sqlAdditionalPropertiesInFabricKeyOptions: Array<ISelectOption<string>> = [
    {
        label: 'Trust server certificate',
        value: 'trustServerCertificate',
        type: Type.Boolean
    },
    {
        label: 'Host name in certificate',
        value: 'hostNameInCertificate',
        type: Type.String
    },
    ... sqlAdditionalPropertiesKeyOptions
];

export function getOracleCommonControls(lsDisplayName: string): ControlMetadata[] {
    return [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'hostName',
                label: stringResources.ModelName_Host,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'port',
                label: stringResources.ModelName_Port,
                validators: [
                    {
                        type: 'pattern',
                        pattern: '^[0-9]*$'
                    }
                ],
                isLabelVertical: true
            }
        },
        {
            type: 'select-switch',
            bind: 'resourceType',
            label: stringResources.Oracle_ResourceTypeLabel,
            description: stringResources.Oracle_ResourceTypeDescription,
            options: [
                {
                    label: stringResources.Oracle_SID_Option_DisplayText,
                    value: 'sid'
                },
                {
                    label: stringResources.Oracle_ServiceName_Option_DisplayText,
                    value: 'serviceName'
                }
            ],
            defaultValue: '{resourceType != null ? resourceType : "sid"}',
            isLabelVertical: true,
            cases: [
                {
                    caseValue: 'sid',
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'sid',
                                label: stringResources.ModelName_Sid,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }],
                                isLabelVertical: true
                            }
                        }
                    ]
                },
                {
                    caseValue: 'serviceName',
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'serviceName',
                                label: stringResources.ModelName_ServiceName,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }],
                                isLabelVertical: true
                            }
                        }
                    ]
                }
            ]
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'userName',
                label: stringResources.ModelName_UserName,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true
            }
        },
        {
            type: 'dynamic-akv-switcher',
            enableAKVParameterization: true,
            hideAKVSwitcherInConnStrProps: true,
            bind: 'password',
            label: stringResources.ModelName_Password,
            child: {
                type: 'parameterizable',
                attribute: 'password',
                child: {
                    type: 'password',
                    label: stringResources.ModelName_Password,
                    required: true,
                    isLabelVertical: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                },
                description: format(
                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                    format(stringResources.ModelName_LinkedServiceTemplate, lsDisplayName),
                    stringResources.ModelName_Password
                )
            }
        }
    ];
}

export function getOracleLinkedServiceControls(lsDisplayName: string): ControlMetadata[] {
    return [
        {
            type: 'select-button-switch',
            required: true,
            isLabelVertical: true,
            options: [
                {
                    label: stringResources.ModelName_ConnectionString,
                    value: false
                },
                {
                    label: stringResources.AzureKeyVault_DisplayText,
                    value: true,
                    hideAKVOptionIfNotChosen: false
                }
            ],
            bind: 'enableAKV',
            isNotRelatedToCredential: true,
            defaultValue: false,
            cases: [
                {
                    caseValue: true,
                    children: [
                        {
                            type: 'azure-key-vault',
                            enableAKVParameterization: true,
                            bind: 'referedAKV',
                            isLabelVertical: true,
                            description: format(
                                stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                                format(stringResources.ModelName_LinkedServiceTemplate, lsDisplayName),
                                'Host=<host>;Port=<port>;Sid=<sid>;User Id=<username>;Password=<password>;'
                            )
                        }
                    ]
                },
                {
                    caseValue: false,
                    children: [
                        ...getOracleCommonControls(lsDisplayName),
                        {
                            type: 'key-val-list',
                            bind: 'additionalFields',
                            label: stringResources.AdditionalConnectionStringFields
                        }
                    ]
                }
            ]
        }
    ];
}

export function oracleSourcePartitionOptions(): Array<IRadioOption<string>> {
    return [
        {
            label: stringResources.None,
            value: partitionOptionConst.none
        },
        {
            label: stringResources.PartitionOptionType_PhysicalPartitionOfTable,
            value: partitionOptionConst.physicalPartitionsOfTable,
            description: format(
                stringResources.Partition_Physical_Tooltip_Template,
                '(?DfTabularPartitionName)',
                'SELECT * FROM <TableName> PARTITION("?DfTabularPartitionName")'
            )
        },
        {
            label: stringResources.PartitionOptionType_DynamicRange,
            value: partitionOptionConst.dyamicRange,
            description: format(
                stringResources.Partition_DynamicRange_Tooltip_Template_s,
                '(?DfRangePartitionColumnName, ?DfRangePartitionLowbound, ?DfRangePartitionUpbound)',
                'SELECT * FROM <TableName> WHERE ?DfRangePartitionColumnName <= ?DfRangePartitionUpbound AND ?DfRangePartitionColumnName >= ?DfRangePartitionLowbound'
            )
        }
    ]
}

export function getOracleSourcePartitionMetadata(datasetType: string): ControlMetadata {
    return {
        type: 'radio-switch',
        label: stringResources.ModelName_Partition_Option,
        defaultValue: partitionOptionConst.none,
        description:
            '<a target="_blank" href="' +
            partitionOptionForwardLinkMap[datasetType] +
            '">' +
            stringResources.LearnMore +
            '</a>',
        escapeDescription: false,
        options: oracleSourcePartitionOptions(),
        bind: 'partitionOption',
        isParameterizable: true,
        cases: [
            {
                caseValue: partitionOptionConst.physicalPartitionsOfTable,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'array-container',
                            bind: 'partitionNames',
                            label: stringResources.ModelName_Partition_Names,
                            isLabelVertical: false,
                            expandable: false,
                            child: {
                                type: 'array-item',
                                children: [
                                    {
                                        type: 'textbox',
                                        bind: 'itemPropertyNamePlaceholder'
                                    }
                                ]
                            }
                        },
                        dynamicContentTextClass: 'input-spaced'
                    }
                ]
            },
            {
                caseValue: partitionOptionConst.dyamicRange,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'columns-selection',
                            bind: 'partitionColumnName',
                            label: stringResources.ModelName_Partition_ColumnName,
                            multiple: false,
                            includeCopySourceProperties: true,
                            allowedColumnTypes: ['Decimal', 'Number', 'Double'],
                            excludeSourceProperties: ['partitionOption', 'partitionSettings']
                        },
                        dynamicContentTextClass: 'input-spaced'
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'partitionUpperBound',
                            label: stringResources.ModelName_Partition_UpperBound
                        },
                        dynamicContentTextClass: 'input-spaced'
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'partitionLowerBound',
                            label: stringResources.ModelName_Partition_LowerBound
                        },
                        dynamicContentTextClass: 'input-spaced'
                    }
                ]
            }
        ]
    };
}

export function getOracleCopySourceControls(datasetType: string): ControlMetadata[] {
    const controls: ControlMetadata[] = [
        getOracleSourcePartitionMetadata(datasetType),
        queryTimeoutMetadata
    ];

    if (!__IS_FABRIC_DPE__) {
        controls.push({
            type: 'parameterizable',
            child: {
                type: 'checkbox',
                bind: 'convertDecimalToInteger',
                label: stringResources.convertDec2Int,
                defaultValue: false,
                description: stringResources.convertDec2IntDesc,
                labelPosition: 'left'
            },
            hide: '{ !owner.sourceDataset.linkedService || !owner.sourceDataset.linkedService.showConvertDecimalToIntegerControl }',
            dynamicContentTextClass: 'input-spaced'
        });
    }

    controls.push(oracleV2NumberSettingsMetadata);

    return controls;
}

export function getOracleCopySinkControls(): ControlMetadata[] {
    return [
        {
            type: 'radio-switch',
            label: stringResources.ModelName_WriteBehavior,
            hide: !GlobalVariables.oracleUpsert,
            bind: 'writeBehavior',
            isParameterizable: true,
            showDynamically: true,
            outsideAdvancedSectionInFabric: true,
            options: [
                {
                    value: writeBehaviorOptionValues.insert,
                    label: stringResources.WriteBehavior_Option_Insert
                },
                {
                    value: writeBehaviorOptionValues.upsert,
                    label: stringResources.WriteBehavior_Option_Upsert
                }
            ],
            defaultValue: writeBehaviorOptionValues.insert,
            cases: [
                {
                    caseValue: writeBehaviorOptionValues.upsert,
                    children: [
                        {
                            type: 'sql-upsert',
                            bind: {
                                keys: 'writeBehaviorKeys'
                            },
                            hasTempDB: false,
                            hasInterimSchemaName: false,
                            keyColumnsAttributeName: 'writeBehaviorKeys',
                            parameterizable: true,
                            required: true
                        }
                    ]
                }
            ]
        },
        preCopyScriptMetadata(),
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'writeBatchTimeout',
                label: stringResources.ModelName_WriteBatchTimeout,
                validators: [
                    {
                        type: 'pattern',
                        pattern: Constants.timeoutValidationPattern,
                        errorMessage: format(
                            stringResources.InvalidTimeoutPattern,
                            stringResources.ModelName_WriteBatchTimeout
                        )
                    }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'writeBatchSize',
                label: stringResources.ModelName_WriteBatchSize,
                inputType: 'number',
                min: 0
            },
            dynamicContentTextClass: 'input-spaced'
        },
        maxConcurrentConnectionsMetadata
    ];
}

function getAuthenticationTypeDescription(
    label: string
): Pick<ISelectSwitchMetadata, 'description' | 'escapeDescription'> | null {
    if (label === stringResources.AmazonRdsSqlServer_DisplayText) {
        return {
            description: stringResources.AmazonRdsSqlServer_AuthenticationTypeDescription,
            escapeDescription: false
        };
    } else {
        return null;
    }
}

function sqlServerAuthChildrenControls(label: string, hideAKVSwitcherInConnStrProps?: boolean): ControlMetadata[] {
    return [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'userName',
                label: stringResources.ModelName_UserName,
                required: true,
                validators: [{ type: 'required', errorMessage: '' }],
                isLabelVertical: true
            }
        },
        {
            type: 'dynamic-akv-switcher',
            enableAKVParameterization: true,
            hideAKVSwitcherInConnStrProps: hideAKVSwitcherInConnStrProps,
            bind: 'password',
            label: stringResources.ModelName_Password,
            child: {
                type: 'parameterizable',
                attribute: 'password',
                child: {
                    type: 'password',
                    label: stringResources.ModelName_Password,
                    required: true,
                    isLabelVertical: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            },
            description: format(
                stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, label),
                stringResources.ModelName_Password
            )
        }
    ];
}

function sqlServerAuthControl(label: string, useConnectionStringAKV?: boolean, isSqlV2?: boolean): ControlMetadata {
    return {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        ...getAuthenticationTypeDescription(label),
        warningMessageCalculator: (value: string) => {
            if (
                value === '' + SqlServerAuthType.ManagedServiceIdentity ||
                value === '' + SqlServerAuthType.UserAssignedManagedIdentity
            ) {
                return format(stringResources.AuthenticationType_OnlyApplyToAzureVms, value);
            }

            return undefined;
        },
        options: isSqlV2 ? [
            {
                label: stringResources.AuthenticationType_Select_SQL_Option,
                value: SqlServerAuthType.SQLAuthentication
            },
            {
                label: stringResources.AuthenticationType_Select_Windows_Option,
                value: SqlServerAuthType.WindowsAuthentication
            },
            // {
            //     label: stringResources.AuthenticationType_ManagedServiceIdentity,
            //     value: SqlServerAuthType.ManagedServiceIdentity
            // },
            {
                label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
                value: SqlServerAuthType.UserAssignedManagedIdentity
            }
        ] : [
            {
                label: stringResources.AuthenticationType_Select_SQL_Option,
                value: SqlServerAuthType.SQLAuthentication
            },
            {
                label: stringResources.AuthenticationType_Select_Windows_Option,
                value: SqlServerAuthType.WindowsAuthentication
            }
        ],
        isLabelVertical: true,
        defaultValue: SqlServerAuthType.SQLAuthentication,
        cases: compact([
            {
                caseValue: SqlServerAuthType.WindowsAuthentication,
                children: sqlServerAuthChildrenControls(label)
            },
            useConnectionStringAKV
                ? undefined
                : {
                    caseValue: SqlServerAuthType.SQLAuthentication,
                    children: sqlServerAuthChildrenControls(label, true)
                },
            {
                caseValue: SqlServerAuthType.ManagedServiceIdentity,
                children: !GlobalVariables.isSamiSupportEnabled
                    ? [
                        {
                            type: 'management-identity-info',
                            resourceType: stringResources.SQLDatabase,
                            link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-database?tabs=data-factory#managed-identity'
                        }
                    ]
                    : [{
                        type: 'radio-switch',
                        bind: 'authReferenceMethod',
                        nested: 1,
                        label: stringResources.Authentication_Reference_Method_Label,
                        description: stringResources.Authentication_Reference_Method_Description,
                        options: authenticationReferenceMethodOptions,
                        isLabelVertical: true,
                        defaultValue: azureSqlAuthRefTypes.InlineRefTypeAuthentication,
                        cases: [
                            {
                                caseValue: azureSqlAuthRefTypes.InlineRefTypeAuthentication,
                                children:
                                    [
                                        // system Assigned Managed Identity
                                        {
                                            type: 'management-identity-info',
                                            resourceType: stringResources.SQLDatabase,
                                            link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-sql-database?tabs=data-factory#managed-identity'
                                        }
                                    ]
                            },
                            {
                                caseValue: azureSqlAuthRefTypes.CredentialRefTypeAuthentication,
                                children: [
                                    {
                                        type: 'credential-select',
                                        bind: 'credential',
                                        filterCredentialType: CredentialTypeOptions.ManagedIdentity,
                                        required: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                ]
                            }
                        ]
                    }]
            },
            {
                caseValue: SqlServerAuthType.UserAssignedManagedIdentity,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            },
        ])
    };
}

function sqlServerV1LinkedServiceControl(label: string): ControlMetadata {
    return {
        type: 'select-button-switch',
        required: true,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.ModelName_ConnectionString,
                value: false
            },
            {
                label: stringResources.AzureKeyVault_DisplayText,
                value: true,
                hideAKVOptionIfNotChosen: false
            }
        ],
        bind: 'enableAKV',
        isNotRelatedToCredential: true,
        defaultValue: false,
        cases: [
            {
                caseValue: true,
                children: [
                    {
                        type: 'azure-key-vault',
                        enableAKVParameterization: true,
                        bind: 'referedAKV',
                        isLabelVertical: true,
                        description: format(
                            stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                            format(stringResources.ModelName_LinkedServiceTemplate, label),
                            'Data Source=<servername>\\<instance name if using named instance>;Initial Catalog=<databasename>;Integrated Security=False;User ID=<username>;Password=<password>;'
                        )
                    },
                    sqlServerAuthControl(label, true),
                    sqlEncryption(label)
                ]
            },
            {
                caseValue: false,
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'serverName',
                            label: stringResources.ModelName_ServerName,
                            required: true,
                            validators: [{ type: 'required', errorMessage: '' }],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'databaseName',
                            label: stringResources.ModelName_DatabaseName,
                            required: true,
                            validators: [{ type: 'required', errorMessage: '' }],
                            isLabelVertical: true
                        }
                    },
                    sqlServerAuthControl(label),
                    sqlEncryption(label),
                    {
                        type: 'key-val-list',
                        bind: 'additionalFields',
                        label: stringResources.AdditionalConnectionStringFields
                    }
                ]
            }
        ]
    };
}

export function getSqlServerLinkedServiceControls(label: string): ControlMetadata[] {
    return [
        {
            type: 'radio-switch',
            bind: 'sqlVersion',
            label: stringResources.VersionLabel,
            isLabelVertical: true,
            options: driverVersionVersionOptions,
            defaultValue: driverVersions.v2,
            cases: [
                {
                    caseValue: driverVersions.v1,
                    children: [sqlServerV1LinkedServiceControl(label)]
                },
                {
                    caseValue: driverVersions.v2,
                    children: [
                        connectionStringParseControl(true),
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'serverName',
                                label: stringResources.ModelName_ServerName,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }],
                                isLabelVertical: true
                            }
                        },
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'databaseName',
                                label: stringResources.ModelName_DatabaseName,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }],
                                isLabelVertical: true
                            }
                        },
                        sqlServerAuthControl(label, false, true),
                        sqlEncryption(label),
                        sqlEncryptControl,
                        sqlTrustServerCertificateControl,
                        {
                            type: 'parameter-config',
                            bind: 'parameterizableAdditionalFields',
                            label: stringResources.AdditionalConnectionStringFields,
                            isLayoutVertical: true,
                            isKeyDropdown: true,
                            keyOptions: sqlAdditionalPropertiesKeyOptions,
                            keyOptionHasType: true,
                            dynamicInputTypes: true,
                            dynamicInputTypesParameterizable: true,
                            keyLabel: stringResources.PropertyName,
                            defaultWidth: 250
                        }
                    ]
                }
            ]
        }
    ];
}

export const stringTimestampConversion = {
    [ColumnType.String]: {
        types: ['DateTime'],
        formats: ['ISO8601', 'D/M/Y H24:M:S', 'D/M/Y H12:M:S', 'M/D/Y H24:M:S', 'M/D/Y H12:M:S']
    }
};

export const cdcDestination = [
    adfModelTypeMap.azureSql.datasetType,
    adfModelTypeMap.sqlServer.datasetType,
    adfModelTypeMap.azureSqlMI.datasetType,
    adfModelTypeMap.fabricSql.datasetType,
    adfModelTypeMap.snowflakeV2.datasetType,
    adfModelTypeMap.lakehouseTable.datasetType,
    adfModelTypeMap.azureSqlDW.datasetType,
    adfModelTypeMap.DataWarehouse.datasetType
]
    .concat(
        GlobalVariables.supportDelta || GlobalVariables.supportDeltaCloneCopyJob
            ? [adfModelTypeMap.delta.datasetType]
            : []
    )
    .concat(GlobalVariables.copyJobOracleCDCSink ? [adfModelTypeMap.oracle.datasetType] : []);

export const sapDatasphereConnectors = [
    adfModelTypeMap.sapDatasphereForAzureDataLakeStorageGen2.linkedServiceType,
    adfModelTypeMap.sapDatasphereForAmazonS3.linkedServiceType,
    adfModelTypeMap.sapDatasphereForGoogleCloudStorage.linkedServiceType
];
